import { redis } from './redis'

const NAMESPACE = process.env.CACHE_NAMESPACE || ''
const CACHE_LOCK_TTL_SECONDS = process.env.REDIS_LOCK_TTL_SECONDS
  ? parseInt(process.env.REDIS_LOCK_TTL_SECONDS, 10)
  : 300

export enum FetchMode {
  FOREGROUND,
  ALWAYS_BACKGROUND,
  BACKGROUND_IF_EXPIRED,
}

interface LookupContext<Args, Type> {
  key: string | ((args: Args) => string)
  ttlSeconds: number
  staleSeconds?: number
  lookup: (args: Args) => Promise<Type> | Type
  forceBackground?: boolean
  fetchMode?: FetchMode
}

interface CachedObject<Type> {
  object: Type
  expires: number
  stale?: number
}

const generateKey = (key: string) => {
  if (!NAMESPACE) {
    return key
  }
  return `${NAMESPACE}-${key}`
}

const lookup = async <A, T extends any>(
  context: LookupContext<A, T>,
  args: A,
) => {
  const lookupPromise = context.lookup(args)
  const object = await Promise.resolve(lookupPromise)

  if (redis && object) {
    const key =
      typeof context.key === 'function' ? context.key(args) : context.key
    const prefixedKey = generateKey(key)
    const expires = new Date().getTime() + context.ttlSeconds * 1000
    const stale = context.staleSeconds
      ? new Date().getTime() + context.staleSeconds * 1000
      : undefined
    const cachedObject: CachedObject<T> = {
      object,
      expires,
      stale,
    }

    await redis.set(
      prefixedKey,
      JSON.stringify(cachedObject, (_, value) =>
        value instanceof Set ? Array.from(value) : value,
      ),
    )
  }

  return object
}

const lock = async (key: string) => {
  if (!redis) {
    return false
  }
  const lockKey = `${key}_lock`
  const locked = await redis.set(
    lockKey,
    '1',
    'EX',
    CACHE_LOCK_TTL_SECONDS,
    'NX',
  )
  return locked === 'OK'
}

const releaseLock = async (key: string) => {
  if (!redis) {
    return
  }
  const lockKey = `${key}_lock`
  await redis.del(lockKey)
}

export const createStore = <Args, Type extends any>(
  context: LookupContext<Args, Type>,
) => {
  return {
    get: async (args: Args): Promise<Type | undefined> => {
      if (!redis) {
        return await lookup(context, args)
      }

      const fetchMode =
        context.fetchMode ||
        (context.forceBackground
          ? FetchMode.ALWAYS_BACKGROUND
          : FetchMode.FOREGROUND)

      const key =
        typeof context.key === 'function' ? context.key(args) : context.key
      const prefixedKey = generateKey(key)
      const data = await redis.get(prefixedKey)
      if (!data) {
        if (
          fetchMode !== FetchMode.BACKGROUND_IF_EXPIRED &&
          !(await lock(prefixedKey))
        ) {
          return undefined
        }

        const lookupPromise = lookup(context, args).finally(() => {
          releaseLock(prefixedKey)
        })

        if (fetchMode === FetchMode.ALWAYS_BACKGROUND) {
          return undefined
        }

        return await lookupPromise
      }

      const parsedData = JSON.parse(data) as CachedObject<Type>
      const now = new Date().getTime()

      if (parsedData.expires < now) {
        if (!(await lock(prefixedKey))) {
          if (parsedData.stale && parsedData.stale > now) {
            return parsedData.object
          }

          if (fetchMode === FetchMode.BACKGROUND_IF_EXPIRED) {
            return parsedData.object
          }

          return undefined
        }

        const lookupPromise = lookup(context, args).finally(() => {
          releaseLock(prefixedKey)
        })

        if (parsedData.stale && parsedData.stale > now) {
          return parsedData.object
        }

        if (
          fetchMode === FetchMode.ALWAYS_BACKGROUND ||
          fetchMode === FetchMode.BACKGROUND_IF_EXPIRED
        ) {
          return parsedData.object
        }

        return await lookupPromise
      }

      return parsedData.object
    },
  }
}
